import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Check, Trash2, ChevronRight, ListChecks, AlertTriangle, CalendarClock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { Title, Empty } from './ui';

/* Lo que hay que hacer hoy. La mayoría sale sola del estado de la plataforma
   —un cobro venció, un aviso espera aprobación— y el resto lo cargo a mano. */

const PRIORIDADES = {
  alta: { label: 'Urgente', color: '#d9455f' },
  media: { label: 'Importante', color: '#d2930c' },
  baja: { label: 'Cuando pueda', color: '#12a37a' },
};
const HORIZONTES = [
  { key: 'dia', label: 'Hoy' },
  { key: 'semana', label: 'Esta semana' },
  { key: 'mes', label: 'Este mes' },
];
const ORIGENES = {
  cobro: 'Cobro', pago: 'Pago', envio: 'Envío', consulta: 'Consulta',
  renovacion: 'Renovación', reporte: 'Reporte', abono: 'Abono', propuesta: 'Propuesta', manual: 'Mía',
};

/* Cuando hay muchas tareas del mismo tipo, una lista plana de 56 líneas no se
   lee: se abandona. Se apilan en una sola fila que se despliega para ir
   tildando. Las mías quedan siempre sueltas: son las que de verdad leo una por una. */
const APILAR_DESDE = 3;
const APILADAS = {
  cobro: 'Recordatorios de cobro', pago: 'Pagos informados por avisar',
  envio: 'Envíos esperando el sí', consulta: 'Consultas sin responder',
  renovacion: 'Renovaciones de hosting', reporte: 'Reportes por subir',
  abono: 'Abonos sin ajustar', propuesta: 'Propuestas sin novedades',
};
const ORDEN_PRIORIDAD = { alta: 0, media: 1, baja: 2 };

function apilar(tareas) {
  const pilas = new Map();
  tareas.forEach(t => {
    // Las manuales no se apilan: cada una lleva su propia clave
    const clave = t.origen === 'manual' || !APILADAS[t.origen] ? `sola:${t.id}` : t.origen;
    if (!pilas.has(clave)) pilas.set(clave, []);
    pilas.get(clave).push(t);
  });
  // El Map mantiene el orden de aparición, así que cada pila queda en el lugar
  // de su tarea más urgente: lo importante sigue arriba
  const filas = [];
  pilas.forEach((items, clave) => {
    if (items.length < APILAR_DESDE) items.forEach(t => filas.push({ tipo: 'tarea', clave: 't' + t.id, t }));
    else filas.push({ tipo: 'pila', clave, items });
  });
  return filas;
}

// "Velsky, Nogueira y 6 más": de un vistazo se sabe de quiénes es la pila
function resumirClientes(items) {
  const nombres = [...new Set(items.map(t => t.cliente).filter(Boolean))];
  if (!nombres.length) return null;
  if (nombres.length <= 2) return nombres.join(' y ');
  return `${nombres.slice(0, 2).join(', ')} y ${nombres.length - 2} más`;
}

export default function Tareas() {
  const [data, setData] = useState(null);
  const [horizonte, setHorizonte] = useState('dia');
  const [prioridad, setPrioridad] = useState('');
  const [verHechas, setVerHechas] = useState(false);
  const [vista, setVista] = useState('lista');       // lista | prioridad
  const [nueva, setNueva] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    const q = new URLSearchParams({ horizonte, ...(prioridad && { prioridad }), ...(verHechas && { hechas: '1' }) });
    return api.get('/agency/tareas?' + q.toString())
      .then(r => setData(r.data))
      .catch(() => toast.error('No se pudieron cargar las tareas'));
  }, [horizonte, prioridad, verHechas]);
  useEffect(() => { load(); }, [load]);

  // Se pinta al instante y se corrige si el guardado falla
  const marcar = async (t, hecha) => {
    setData(d => ({ ...d, tareas: d.tareas.map(x => (x.id === t.id ? { ...x, estado: hecha ? 'hecha' : 'pendiente' } : x)) }));
    try { await api.put('/agency/tareas/' + t.id, { estado: hecha ? 'hecha' : 'pendiente' }); load(); }
    catch { toast.error('No se pudo guardar'); load(); }
  };
  const borrar = async (t) => {
    if (!window.confirm(`¿Borrar "${t.titulo}"?`)) return;
    try { await api.delete('/agency/tareas/' + t.id); load(); } catch { toast.error('No se pudo borrar'); }
  };
  const crear = async (e) => {
    e.preventDefault();
    try {
      await api.post('/agency/tareas', nueva);
      toast.success('Tarea agregada'); setNueva(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo crear'); }
  };

  const [abiertas, setAbiertas] = useState(() => new Set());
  const alternar = (k) => setAbiertas(s => {
    const n = new Set(s);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const fila = (t, dentro = false) => {
    const p = PRIORIDADES[t.prioridad] || PRIORIDADES.media;
    const hecha = t.estado === 'hecha';
    return (
      <div key={t.id} className={'tarea' + (hecha ? ' hecha' : '') + (dentro ? ' anidada' : '')} style={{ '--tono': p.color }}>
        <button className="tarea-check" onClick={() => marcar(t, !hecha)}
          aria-label={hecha ? 'Marcar como pendiente' : 'Marcar como hecha'}>
          {hecha && <Check size={13} />}
        </button>

        <div className="tarea-cuerpo" onClick={() => t.url && navigate(t.url)}
          style={{ cursor: t.url ? 'pointer' : 'default' }}>
          <div className="tarea-titulo">{t.titulo}</div>
          {t.detalle && <div className="tarea-detalle">{t.detalle}</div>}
          <div className="tarea-pie">
            {!dentro && <span className="tarea-origen">{ORIGENES[t.origen] || t.origen}</span>}
            {t.atraso > 0 && <span className="tarea-atraso">atrasada {t.atraso} día{t.atraso === 1 ? '' : 's'}</span>}
            {t.cliente && <span>{t.cliente}</span>}
          </div>
        </div>

        <div className="tarea-acciones">
          {t.origen === 'manual' && (
            <button className="btn btn-ghost btn-sm" onClick={() => borrar(t)} title="Borrar"><Trash2 size={14} /></button>
          )}
          {t.url && <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
        </div>
      </div>
    );
  };

  const c = data?.conteo || {};
  const tareas = data?.tareas || [];
  const grupos = vista === 'prioridad'
    ? Object.keys(PRIORIDADES).map(k => ({ key: k, label: PRIORIDADES[k].label, items: tareas.filter(t => t.prioridad === k) }))
    : [{ key: 'todas', label: null, items: tareas }];

  return (
    <div>
      <Title right={
        <button className="btn btn-primary btn-sm" onClick={() => setNueva({ titulo: '', detalle: '', prioridad: 'media', horizonte: 'dia' })}>
          <Plus size={15} /> Nueva tarea
        </button>
      }>Tareas</Title>

      {/* Resumen */}
      <div className="tareas-resumen">
        <div className="tareas-chip" data-tono="alta">
          <AlertTriangle size={16} /><b>{c.urgentes ?? 0}</b> urgente{c.urgentes === 1 ? '' : 's'}
        </div>
        <div className="tareas-chip" data-tono={c.atrasadas ? 'alta' : 'neutro'}>
          <CalendarClock size={16} /><b>{c.atrasadas ?? 0}</b> atrasada{c.atrasadas === 1 ? '' : 's'}
        </div>
        <div className="tareas-chip" data-tono="ok">
          <Check size={16} /><b>{c.hechas_hoy ?? 0}</b> hecha{c.hechas_hoy === 1 ? '' : 's'} hoy
        </div>
      </div>

      {/* Filtros */}
      <div className="tareas-barra">
        <div className="solapas">
          {HORIZONTES.map(h => (
            <button key={h.key} onClick={() => setHorizonte(h.key)} className={horizonte === h.key ? 'on' : ''}>
              {h.label}<span>{c[h.key] ?? 0}</span>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
          <select value={prioridad} onChange={e => setPrioridad(e.target.value)} style={{ width: 'auto' }}>
            <option value="">Toda prioridad</option>
            {Object.entries(PRIORIDADES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div className="solapas chicas">
            <button onClick={() => setVista('lista')} className={vista === 'lista' ? 'on' : ''}>Lista</button>
            <button onClick={() => setVista('prioridad')} className={vista === 'prioridad' ? 'on' : ''}>Por prioridad</button>
          </div>
          <label className="tareas-ver-hechas">
            <input type="checkbox" checked={verHechas} onChange={e => setVerHechas(e.target.checked)} /> Ver hechas
          </label>
        </div>
      </div>

      {!data ? <div className="card" style={{ color: 'var(--text-muted)' }}>Cargando...</div>
        : tareas.length === 0 ? (
          <div className="card"><Empty>
            {verHechas ? 'No hay tareas para mostrar' : '¡No te queda nada pendiente acá!'}
          </Empty></div>
        ) : grupos.map(g => g.items.length === 0 ? null : (
          <div key={g.key} style={{ marginBottom: 18 }}>
            {g.label && (
              <div className="tareas-grupo" style={{ color: PRIORIDADES[g.key]?.color }}>
                <span className="punto" style={{ background: PRIORIDADES[g.key]?.color }} />
                {g.label}<em>{g.items.length}</em>
              </div>
            )}
            <div className="tareas-lista">
              {apilar(g.items).map(f => {
                if (f.tipo === 'tarea') return fila(f.t);

                const pendientes = f.items.filter(t => t.estado !== 'hecha');
                const atrasadas = pendientes.filter(t => t.atraso > 0).length;
                const tono = PRIORIDADES[[...f.items].sort(
                  (a, b) => ORDEN_PRIORIDAD[a.prioridad] - ORDEN_PRIORIDAD[b.prioridad])[0]?.prioridad]?.color;
                const abierta = abiertas.has(f.clave);
                const clientes = resumirClientes(pendientes.length ? pendientes : f.items);

                return (
                  <div key={f.clave} className={'tarea-pila' + (abierta ? ' abierta' : '')} style={{ '--tono': tono }}>
                    <button className="tarea-pila-cab" onClick={() => alternar(f.clave)} aria-expanded={abierta}>
                      <ChevronRight size={16} className="tarea-pila-flecha" />
                      <span className="tarea-pila-cuenta">{pendientes.length || f.items.length}</span>
                      <span className="tarea-pila-texto">
                        <span className="tarea-titulo">{APILADAS[f.clave] || ORIGENES[f.clave]}</span>
                        <span className="tarea-pie">
                          {atrasadas > 0 && <span className="tarea-atraso">{atrasadas} atrasada{atrasadas === 1 ? '' : 's'}</span>}
                          {clientes && <span>{clientes}</span>}
                        </span>
                      </span>
                      <span className="tarea-pila-accion">{abierta ? 'Cerrar' : 'Ver y tildar'}</span>
                    </button>
                    {abierta && (
                      <div className="tareas-anidadas">
                        {f.items.map(t => fila(t, true))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

      {nueva && (
        <div className="modal-overlay" onClick={() => setNueva(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2>Nueva tarea</h2>
            <form onSubmit={crear}>
              <div className="form-group"><label>Qué hay que hacer</label>
                <input value={nueva.titulo} autoFocus required maxLength={250}
                  onChange={e => setNueva({ ...nueva, titulo: e.target.value })} /></div>
              <div className="form-group"><label>Detalle <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· opcional</span></label>
                <input value={nueva.detalle} maxLength={500}
                  onChange={e => setNueva({ ...nueva, detalle: e.target.value })} /></div>
              <div className="form-group"><label>Prioridad</label>
                <div className="elegir">
                  {Object.entries(PRIORIDADES).map(([k, v]) => (
                    <button type="button" key={k} onClick={() => setNueva({ ...nueva, prioridad: k })}
                      className={nueva.prioridad === k ? 'on' : ''} style={{ '--tono': v.color }}>
                      <span className="punto" style={{ background: v.color }} />{v.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group"><label>Para cuándo</label>
                <div className="elegir">
                  {HORIZONTES.map(h => (
                    <button type="button" key={h.key} onClick={() => setNueva({ ...nueva, horizonte: h.key })}
                      className={nueva.horizonte === h.key ? 'on' : ''}>{h.label}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setNueva(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary"><ListChecks size={15} /> Agregar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
